import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface PieChartData {
  id: string;
  label: string;
  count: number;
  color: string;
  percentage: number;
}

interface PieChartProps {
  title: string;
  data: PieChartData[];
}

// Custom tooltip with proper styling
function CustomTooltip({ active, payload }: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-gray-900 text-white text-xs rounded px-2 py-1.5 shadow-lg">
        <span className="font-medium">{data.label}: {data.count}</span>
        <span className="text-gray-300 ml-1">({data.percentage}%)</span>
      </div>
    );
  }
  return null;
}

export function PieChart({ title, data }: PieChartProps) {
  const total = data.reduce((sum, d) => sum + d.count, 0);
  const filteredData = data.filter(d => d.count > 0);

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 h-full">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">
        {title}
      </h3>

      {total === 0 ? (
        <div className="text-sm text-gray-500 py-4 text-center">
          No data to display
        </div>
      ) : (
        <div className="flex items-center gap-6">
          {/* Recharts Pie with Center Label */}
          <div className="w-36 h-36 flex-shrink-0 relative">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPie>
                <Pie
                  data={filteredData}
                  dataKey="count"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={44}
                  outerRadius={68}
                  paddingAngle={1}
                  animationDuration={300}
                  animationBegin={0}
                >
                  {filteredData.map((entry) => (
                    <Cell key={entry.id} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </RechartsPie>
            </ResponsiveContainer>
            {/* Center Total Label */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-xl font-bold text-gray-900">{total}</span>
            </div>
          </div>

          {/* Legend — 2-column grid */}
          <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-1.5 min-w-0">
            {data.map((item) => (
              <div key={item.id} className="flex items-center gap-2 min-w-0">
                <div
                  className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-gray-600 truncate flex-1">
                  {item.label}
                </span>
                <span className="text-xs font-medium text-gray-900 flex-shrink-0">
                  {item.count > 0 ? `${item.percentage}%` : '-'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
